import {
  getPageOptions,
  getReachablePagesFrom,
  isOptionSelectionPage,
  pages,
  resolveClicksToTargets,
} from '../../context/pages';
import { VISUALISER_ROUTE_META } from '../route-meta';

export type OptionNode = {
  clicksTo?: unknown;
};

type UvInsertConfigLike = {
  active: boolean;
};

type OptionVisibilityResult = {
  hiddenOptionIndexes: Set<number>;
  visibleOptionIndexes: number[];
};

/**
 * Returns all UV insert config handles reachable from a single option `clicksTo`.
 */
export function getReachableConfigHandlesForClicksTo(clicksTo: unknown): string[] {
  const destinations = resolveClicksToTargets(clicksTo);
  if (destinations.length === 0) return [];

  const handles = new Set<string>();
  for (const dest of destinations) {
    const reachable = getReachablePagesFrom({ fromPage: dest });
    for (const page of reachable) {
      const meta = VISUALISER_ROUTE_META[page];
      if (meta?.configHandle) {
        handles.add(meta.configHandle);
      }
    }
  }
  return Array.from(handles);
}

/**
 * Classifies which options should be hidden based on whether their reachable UV insert leaves are active.
 * An option is hidden only when it maps to one or more known configs and all of them are inactive.
 */
export function getOptionVisibilityForOptions(options: OptionNode[], configByHandle: Map<string, UvInsertConfigLike>): OptionVisibilityResult {
  const hiddenOptionIndexes = new Set<number>();
  const visibleOptionIndexes: number[] = [];

  options.forEach((option, index) => {
    const configHandles = getReachableConfigHandlesForClicksTo(option?.clicksTo);
    if (configHandles.length === 0) {
      visibleOptionIndexes.push(index);
      return;
    }

    const matchedConfigs = configHandles
      .map((handle) => configByHandle.get(handle))
      .filter((config): config is UvInsertConfigLike => Boolean(config));

    if (matchedConfigs.length === 0) {
      visibleOptionIndexes.push(index);
      return;
    }

    const hasAnyActiveConfig = matchedConfigs.some((config) => config.active);
    if (hasAnyActiveConfig) {
      visibleOptionIndexes.push(index);
      return;
    }

    hiddenOptionIndexes.add(index);
  });

  // Safety guard: never blank a page due to visibility filtering.
  if (options.length > 0 && visibleOptionIndexes.length === 0) {
    return {
      hiddenOptionIndexes: new Set<number>(),
      visibleOptionIndexes: options.map((_, index) => index),
    };
  }

  return { hiddenOptionIndexes, visibleOptionIndexes };
}

/**
 * Finds the destination page when the current page (and optionally downstream option pages) collapse
 * to a single visible option. Returns null when no auto-skip should happen.
 */
export function resolveSinglePathDestination(options: {
  pageName: string;
  configByHandle: Map<string, UvInsertConfigLike>;
}): string | null {
  const { pageName, configByHandle } = options;
  let currentPage = pageName;
  const seenPages = new Set<string>();

  for (let depth = 0; depth < 20; depth += 1) {
    if (seenPages.has(currentPage)) return null;
    seenPages.add(currentPage);

    const pageOptions = getPageOptions(currentPage) as OptionNode[];
    if (!pageOptions.length) return null;

    const visibility = getOptionVisibilityForOptions(pageOptions, configByHandle);
    if (visibility.visibleOptionIndexes.length !== 1) return null;

    const onlyOption = pageOptions[visibility.visibleOptionIndexes[0]];
    const destinations = resolveClicksToTargets(onlyOption?.clicksTo);
    if (destinations.length !== 1) return null;

    const destination = destinations[0];
    if (!isOptionSelectionPage(destination)) {
      return destination;
    }

    currentPage = destination;
  }

  return null;
}

/**
 * Resolves back navigation through collapsed option pages.
 * If target page has exactly one visible option that routes back to `currentPage`,
 * skip that target and continue to its `backTo` ancestor.
 */
export function resolveBackDestinationThroughCollapsedOptions(options: {
  currentPage: string;
  destinationPage: string;
  configByHandle: Map<string, UvInsertConfigLike>;
}): string {
  const { currentPage, configByHandle } = options;
  let destinationPage = options.destinationPage;
  const seen = new Set<string>();

  for (let depth = 0; depth < 20; depth += 1) {
    if (!isOptionSelectionPage(destinationPage)) {
      return destinationPage;
    }
    if (seen.has(destinationPage)) {
      return destinationPage;
    }
    seen.add(destinationPage);

    const pageOptions = getPageOptions(destinationPage) as OptionNode[];
    const visibility = getOptionVisibilityForOptions(pageOptions, configByHandle);
    if (visibility.visibleOptionIndexes.length !== 1) {
      return destinationPage;
    }

    const onlyOption = pageOptions[visibility.visibleOptionIndexes[0]];
    const destinations = resolveClicksToTargets(onlyOption?.clicksTo);
    if (destinations.length !== 1) {
      return destinationPage;
    }

    // This collapsed page only points back to where we currently are; skip it.
    if (destinations[0] === currentPage) {
      const backTo = pages[destinationPage]?.backTo;
      if (typeof backTo !== 'string' || backTo.length === 0) {
        return destinationPage;
      }
      destinationPage = backTo;
      continue;
    }

    return destinationPage;
  }

  return destinationPage;
}
